import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { MagnifyingGlass } from '@phosphor-icons/react';

const API = '/api';

export function Autocomplete({ endpoint, value, onChange, placeholder, renderItem, onSelect, displayKey, className, 'data-testid': testId }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = async (q) => {
    if (!q || q.length < 1) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${endpoint}`, {
        params: { q },
        withCredentials: true
      });
      setResults(data);
      setIsOpen(data.length > 0);
      setHighlightIdx(-1);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 200);
  };

  const handleSelect = (item) => {
    if (onSelect) {
      onSelect(item);
    }
    setIsOpen(false);
    setHighlightIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(results[highlightIdx]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleFocus = () => {
    if (query && query.length >= 1) {
      search(query);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      <div className="relative">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`pl-8 rounded-lg ${className || ''}`}
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      {isOpen && results.length > 0 && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {results.map((item, idx) => (
            <div
              key={item.id || item[displayKey] || `result-${idx}`}
              className={`px-3 py-2 cursor-pointer transition-colors text-sm ${
                idx === highlightIdx ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50'
              }`}
              onClick={() => handleSelect(item)}
              data-testid={`${testId}-option-${idx}`}
            >
              {renderItem ? renderItem(item) : (
                <span>{displayKey ? item[displayKey] : JSON.stringify(item)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
